import { merge } from 'lodash';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { UserType } from 'api/models';
import { PartnerAPI } from 'api/partner';
import {
  CreatePartnerPayload,
  PartnerCompany,
  PartnerPermission,
  PartnerUser,
} from 'api/partner/types';
import { GENERAL_API_STALE } from 'config/query-config';
import { MembersResponse } from 'data/members/api';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import {
  BaseMembersQueryOptions,
  MembersQueryOptions,
  MembersQueryResult,
  ALL_USERS_QUERY_KEY,
  ALL_MEMBERS_QUERY_KEY,
} from 'queries/members';

export const ALL_PARTNER_COMPANIES_KEY = ['companies', 'partner'];

export const useGetAllPartnerCompanies = (
  queryOptions: UseQueryOptions<
    PartnerCompany[],
    unknown,
    PartnerCompany[],
    typeof ALL_PARTNER_COMPANIES_KEY
  > = {},
): UseQueryResult<PartnerCompany[]> => {
  const isCurrentUserPartner = useIsCurrentUserPartner();

  return useQuery(
    ALL_PARTNER_COMPANIES_KEY,
    () => PartnerAPI.getAllPartnerCompanies(),
    {
      enabled: !isCurrentUserPartner,
      refetchOnMount: true,
      staleTime: GENERAL_API_STALE,
      ...queryOptions,
    },
  );
};

export const useGetAllActivePartnerCompanies = (
  queryOptions: UseQueryOptions<
    PartnerCompany[],
    unknown,
    PartnerCompany[],
    typeof ALL_PARTNER_COMPANIES_KEY
  > = {},
): UseQueryResult<PartnerCompany[]> =>
  useGetAllPartnerCompanies({
    select: (partnerCompanies) =>
      partnerCompanies.filter(({ active }) => active === true),
    ...queryOptions,
  });
export const getPartnerCompanyQueryKey = (companyId: number) => [
  'companies',
  'partner',
  companyId,
];

export const useGetPartnerCompany = (
  companyId: number,
  queryOptions: UseQueryOptions<
    PartnerCompany,
    unknown,
    PartnerCompany,
    ReturnType<typeof getPartnerCompanyQueryKey>
  > = {},
): UseQueryResult<PartnerCompany> => {
  const queryClient = useQueryClient();

  return useQuery(
    getPartnerCompanyQueryKey(companyId),
    () => PartnerAPI.getPartnerCompany(companyId),
    {
      refetchOnMount: true,
      onSuccess: (data) => {
        queryClient.setQueryData<PartnerCompany[]>(
          ALL_PARTNER_COMPANIES_KEY,
          (previousState) =>
            (previousState ?? []).map((x) =>
              x.companyId === companyId ? merge(x, data) : x,
            ),
        );
      },
      ...queryOptions,
    },
  );
};

const ALL_PARTNER_COMPANIES_PERMISSION_KEY = [
  'companies',
  'partner',
  'permissions',
];

export const useGetAllPartnerPermissions = (
  queryOptions: UseQueryOptions<
    PartnerPermission[],
    unknown,
    PartnerPermission[],
    typeof ALL_PARTNER_COMPANIES_PERMISSION_KEY
  > = {},
): UseQueryResult<PartnerPermission[]> =>
  useQuery(
    ALL_PARTNER_COMPANIES_PERMISSION_KEY,
    () => PartnerAPI.getAllPartnerPermissions(),
    {
      refetchOnMount: true,
      ...queryOptions,
    },
  );

export const useCreatePartnerCompanyMutation = (): UseMutationResult<
  PartnerCompany,
  unknown,
  CreatePartnerPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<PartnerCompany, unknown, CreatePartnerPayload>(
    (payload) => PartnerAPI.createPartnerCompany(payload),
    {
      onSuccess: (createdCompany) => {
        queryClient.setQueryData(
          ALL_PARTNER_COMPANIES_KEY,
          (prev?: PartnerCompany[]) => [...(prev ?? []), createdCompany],
        );
        queryClient.invalidateQueries(ALL_USERS_QUERY_KEY);
        queryClient.invalidateQueries(ALL_MEMBERS_QUERY_KEY);
      },
    },
  );
};
export const useUpdatePartnerCompanyMutation = (): UseMutationResult<
  PartnerCompany,
  unknown,
  PartnerCompany
> => {
  const queryClient = useQueryClient();

  return useMutation<PartnerCompany, unknown, PartnerCompany>(
    (payload) => PartnerAPI.updatePartnerCompany(payload),
    {
      onSuccess: (company) => {
        queryClient.setQueryData(
          ALL_PARTNER_COMPANIES_KEY,
          (prev?: PartnerCompany[]) =>
            (prev ?? []).map((x) =>
              x.companyId === company.companyId ? { ...x, ...company } : x,
            ),
        );
      },
    },
  );
};
export const useDeletePartnerCompanyMutation = (): UseMutationResult<
  unknown,
  unknown,
  PartnerCompany
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, PartnerCompany>(
    (payload) => PartnerAPI.deletePartnerCompany(payload),
    {
      onSuccess: (_, company) => {
        queryClient.setQueryData(
          ALL_PARTNER_COMPANIES_KEY,
          (prev?: PartnerCompany[]) =>
            (prev ?? []).filter((x) => x.companyId !== company.companyId),
        );
      },
    },
  );
};

type AllUsersINPartnerKeyType = [string, number, string];
export const getAlluserInPartnerQueryKey = (
  companyId: number,
): AllUsersINPartnerKeyType => ['partner', companyId, 'users'];

export const useAllUserInPartnerCompanyQuery = (
  companyId: number,
  queryOptions: UseQueryOptions<
    PartnerUser[],
    unknown,
    PartnerUser[],
    AllUsersINPartnerKeyType
  > = {},
): UseQueryResult<PartnerUser[]> =>
  useQuery(
    getAlluserInPartnerQueryKey(companyId),
    () => PartnerAPI.getAllUserInPartnerCompany(companyId),
    {
      refetchOnMount: true,
      ...queryOptions,
    },
  );

export const useAllPartnerCompanyMembers = (
  companyId: number,
  {
    queryKey,
    queryFn,
    staleTime,
    select,
    ...restOptions
  }: MembersQueryOptions = {},
): MembersQueryResult => {
  return useQuery({
    ...BaseMembersQueryOptions,
    select: ({ users, members, ...rest }) => {
      const result: MembersResponse = {
        ...rest,
        users: (users ?? []).filter(
          ({ userType, company: { companyId: userCompanyId } }) =>
            userType === UserType.Partner && companyId === userCompanyId,
        ),
        members: (members ?? []).filter(
          ({
            user: {
              userType,
              company: { companyId: userCompanyId },
            },
          }) => userType === UserType.Partner && companyId === userCompanyId,
        ),
      };

      return result;
    },
    refetchOnMount: true,
    ...restOptions,
  });
};
